import React from "react";
import { Row, Col, Tag, Typography, Divider, Empty, Space, Table } from "antd";
import {
  UserOutlined,
  IdcardOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import THEME from "../theme";
import StyledCard from "../components/StyledCard";
import { formatMoney } from "../utils";

const { Text } = Typography;

const getPersonLabel = (record) => {
  if (record.garno === 0 && !record.relationCustomerId) return "ผู้กู้";
  if (record.garno === 0 && record.relationCustomerId) return "คู่สมรสผู้กู้";
  if (record.garno !== 0 && record.relationCustomerId) return "คู่สมรสผู้ค้ำ";
  return `ผู้ค้ำประกัน ${record.garno}`;
};

const InvestigationInfoTab = ({ judgeData }) => {
  const asset = judgeData?.asset ?? [];

  // garno=0 และ relationCustomerId=null → ผู้กู้หลัก
  const mainBorrower = asset.filter(
    (r) => r.garno === 0 && r.relationCustomerId === null,
  );

  // garno=0 แต่มี relationCustomerId → คู่สมรสของผู้ค้ำ
  const spouseMap = {};
  asset
    .filter((r) => r.garno === 0 && r.relationCustomerId !== null)
    .forEach((r) => {
      spouseMap[r.relationCustomerId] = r;
    });

  // guarantors เรียงตาม garno แล้ว insert คู่สมรสหลังแต่ละคน
  const guarantorsSorted = asset
    .filter((r) => r.garno > 0)
    .sort((a, b) => a.garno - b.garno);

  const guarantors = [];
  guarantorsSorted.forEach((g) => {
    guarantors.push(g);
    const spouse = spouseMap[g.customerId];
    if (spouse) guarantors.push(spouse);
  });

  // ค่าสืบทรัพย์: เรียงตาม garno, garno เท่ากันให้ไม่มี relation ขึ้นก่อน
  const moneySorted = [...asset].sort((a, b) => {
    const garnoA = a.garno ?? 0;
    const garnoB = b.garno ?? 0;
    if (garnoA !== garnoB) return garnoA - garnoB;
    return (a.relation ? 1 : 0) - (b.relation ? 1 : 0);
  });

  const totalInvestigationFee = asset.reduce(
    (sum, r) => sum + Number(r.money?.total || 0),
    0,
  );

  const PersonCard = ({ record }) => {
    const hasAsset = record.assetLand?.length > 0;
    const isSpouseOfGuarantor =
      record.garno === 0 && record.relationCustomerId !== null;

    return (
      <StyledCard
        title={
          <Space>
            <UserOutlined
              style={{
                color:
                  record.garno === 0 && !isSpouseOfGuarantor
                    ? THEME.primary
                    : THEME.warning,
              }}
            />
            <Text strong style={{ fontSize: 14 }}>
              {record.garno === 0 && !isSpouseOfGuarantor
                ? `ผู้กู้หลัก`
                : record.garno === 0 && isSpouseOfGuarantor
                  ? `คู่สมรสผู้ค้ำ`
                  : `ผู้ค้ำประกัน ${record.garno}`}
            </Text>
          </Space>
        }
        style={{ marginBottom: 12 }}
      >
        <div
          style={{
            background: hasAsset
              ? "linear-gradient(135deg, #f0f7ff 0%, #f8fafc 100%)"
              : "linear-gradient(135deg, #fff0f0 0%, #fff8f8 100%)",
            borderRadius: 10,
            padding: "12px 16px",
            marginBottom: 14,
            border: hasAsset ? "1px solid #dbeafe" : "1px solid #fecaca",
          }}
        >
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={12}>
              <Space align="start">
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    background: hasAsset ? THEME.primary : "#ef4444",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <UserOutlined style={{ color: "#fff", fontSize: 16 }} />
                </div>
                <div>
                  <Text
                    type="secondary"
                    style={{ fontSize: 14, display: "block" }}
                  >
                    ชื่อ-นามสกุล
                  </Text>
                  <Text
                    strong
                    style={{
                      fontSize: 16,
                      color: hasAsset ? undefined : "#ef4444",
                    }}
                  >
                    {`${record.SNAM}${record.NAME1} ${record.NAME2}`}
                  </Text>
                </div>
              </Space>
            </Col>
            <Col xs={24} sm={12}>
              <Space align="start">
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    background: THEME.info,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <IdcardOutlined style={{ color: "#fff", fontSize: 16 }} />
                </div>
                <div>
                  <Text
                    type="secondary"
                    style={{ fontSize: 14, display: "block" }}
                  >
                    เลขบัตรประชาชน
                  </Text>
                  <Text strong style={{ fontSize: 16, letterSpacing: "0.5px" }}>
                    {record.CUSCOD ?? "-"}
                  </Text>
                </div>
              </Space>
            </Col>
          </Row>
        </div>
        <div
          style={{
            borderRadius: 10,
            border: "1px solid #e8ecf0",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              background: "linear-gradient(135deg, #1e3a5f 0%, #2d6a9f 100%)",
              padding: "10px 16px",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span style={{ fontSize: 16 }}>🏞️</span>
            <Text strong style={{ color: "#fff", fontSize: 14 }}>
              ข้อมูลที่ดิน
            </Text>
            <Tag
              color={hasAsset ? "blue" : "red"}
              style={{ marginLeft: "auto", borderRadius: 20 }}
            >
              {record.assetLand?.length ?? 0} แปลง
            </Tag>
          </div>
          <div style={{ padding: "12px 14px", background: "#f8fafc" }}>
            {!hasAsset ? (
              <div style={{ textAlign: "center", padding: "16px 0" }}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  ไม่มีข้อมูลที่ดิน
                </Text>
              </div>
            ) : (
              record.assetLand.map((land, i) => (
                <div
                  key={land.landId}
                  style={{
                    background: "#fff",
                    borderRadius: 8,
                    padding: "10px 14px",
                    marginBottom: i < record.assetLand.length - 1 ? 10 : 0,
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 8,
                      gap: 8,
                    }}
                  >
                    <div
                      style={{
                        background: THEME.primary,
                        color: "#fff",
                        borderRadius: 20,
                        padding: "1px 10px",
                        fontSize: 11,
                        fontWeight: 600,
                      }}
                    >
                      แปลงที่ {i + 1}
                    </div>
                    <Tag
                      color={land.typeLand === 1 ? "geekblue" : "default"}
                      style={{ fontSize: 11 }}
                    >
                      {land.typeLand === 1 ? "โฉนด" : "อื่นๆ"}
                    </Tag>
                  </div>
                  <Row gutter={[8, 6]}>
                    {[
                      { label: "เลขที่", value: land.numberLand, icon: "🏛️" },
                      {
                        label: "ตำบล",
                        value: land.subDistDesc ?? land.subdistrict,
                        icon: "🗾",
                      },
                      {
                        label: "อำเภอ",
                        value: land.distDesc ?? land.district,
                        icon: "🏛️",
                      },
                      {
                        label: "จังหวัด",
                        value: land.provDesc ?? land.province,
                        icon: "🗺️",
                      },
                    ].map(({ label, value, icon }) => (
                      <Col xs={12} sm={6} key={label}>
                        <div
                          style={{
                            background: "#f8fafc",
                            borderRadius: 6,
                            padding: "5px 10px",
                            border: "1px solid #e8ecf0",
                          }}
                        >
                          <Text
                            type="secondary"
                            style={{ fontSize: 14, display: "block" }}
                          >
                            {icon} {label}
                          </Text>
                          <Text style={{ fontSize: 16, fontWeight: 500 }}>
                            {value ?? "-"}
                          </Text>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              ))
            )}
          </div>
        </div>
      </StyledCard>
    );
  };

  if (asset?.length === 0) return <Empty />;
  return (
    <>
      {mainBorrower.map((r) => (
        <PersonCard key={r.customerId} record={r} />
      ))}
      {guarantors?.length > 0 && (
        <>
          <Divider orientation="left">
            <Text strong style={{ color: THEME.warning }}>
              ผู้ค้ำประกัน ({guarantorsSorted?.length} คน)
            </Text>
          </Divider>
          {guarantors.map((r) => (
            <PersonCard key={r.customerId} record={r} />
          ))}
        </>
      )}
      <StyledCard
        title="ค่าสืบทรัพย์"
        icon={<DollarOutlined style={{ color: THEME.warning, fontSize: 18 }} />}
        style={{ marginTop: 12 }}
      >
        <Table
          dataSource={moneySorted}
          rowKey="assetOnlineId"
          pagination={false}
          size="small"
          columns={[
            {
              title: "#",
              key: "index",
              width: 10,
              align: "center",
              render: (_, __, idx) => idx + 1,
            },
            {
              title: "ชื่อ-นามสกุล",
              key: "name",
              render: (_, record) => (
                <Text>{`${record.SNAM}${record.NAME1} ${record.NAME2}`}</Text>
              ),
            },
            {
              title: "สถานะ",
              key: "status",
              render: (_, record) => (
                <Space size={4} wrap>
                  <Tag color={record.garno === 0 ? "blue" : "orange"}>
                    {getPersonLabel(record)}
                  </Tag>
                  {/* {record.relation && <Tag>{record.relation}</Tag>} */}
                </Space>
              ),
            },
            {
              title: "สถานะทรัพย์",
              key: "assetLand",
              render: (_, record) => (
                <Space size={4} wrap>
                  <Tag color={record?.assetLand?.length > 0 ? "blue" : "red"}>
                    {record?.assetLand?.length > 0
                      ? "เจอทรัพย์"
                      : "ไม่เจอทรัพย์"}
                  </Tag>
                </Space>
              ),
            },
            {
              title: "ทร.14",
              key: "tr14",
              align: "right",
              render: (_, record) => formatMoney(record.money?.tr14),
            },
            {
              title: "ทะเบียนครอบครัว",
              key: "familyRegistration",
              align: "right",
              render: (_, record) =>
                formatMoney(record.money?.familyRegistration),
            },
            {
              title: "ค่าดำเนินการ",
              key: "operationFee",
              align: "right",
              render: (_, record) => formatMoney(record.money?.operationFee),
            },
            {
              title: "ค่าตรวจเพิ่ม",
              key: "additionalFee",
              align: "right",
              render: (_, record) => formatMoney(record.money?.additionalFee),
            },
            {
              title: "รวม",
              key: "total",
              align: "right",
              render: (_, record) => (
                <Text strong style={{ color: THEME.success }}>
                  {formatMoney(record.money?.total)}
                </Text>
              ),
            },
          ]}
          summary={() => (
            <Table.Summary.Row style={{ background: THEME.light }}>
              <Table.Summary.Cell colSpan={8}>
                <Text strong>รวมค่าสืบทรัพย์ทั้งหมด</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell align="right">
                <Text strong style={{ color: THEME.success }}>
                  {formatMoney(totalInvestigationFee)}
                </Text>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
      </StyledCard>
    </>
  );
};

export default InvestigationInfoTab;
